import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HtmlParseService } from '@commons/html-parser/html-parse.service';
import { AlgoliaTrackerInterface, EventTrackerInterface } from '@commons/tracker/event-tracker/event-tracker.interface';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { RouterLinkConfig } from '@core/services/router-extension.service';
import { secureExternalLinks } from '@core/utils/utils';
import { SocialMedia, socialMedia } from '@domains/job/fullweight/share-popup/social-media';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Job } from '@models/job/job';
import { Bookmark } from '@models/profile/bookmark';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import { JobRoutingEnum } from '@routing/job/job-routing.enum';
import { BookmarkToggle } from '@store/profile/bookmark/bookmark.actions';
import { BookmarkState } from '@store/profile/bookmark/bookmark.state';
import { SessionState } from '@store/session/session.state';
import { JobWebservice } from '@webservices/job-api/job.webservice';
import { buildPath, buildUrl, WINDOW } from '@wizbii/angular-utilities';
import dayjs from 'dayjs';
import { Request } from 'express';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, take } from 'rxjs/operators';

interface ShareLink {
  media: SocialMedia;
  url: SafeUrl;
}

@Component({
  selector: 'job-fullweight',
  templateUrl: './fullweight.component.html',
  styleUrls: ['./fullweight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FullweightComponent implements OnInit {
  @Select(SessionState.isLogged) isLogged$: Observable<boolean>;
  @Select(BookmarkState) bookmarkMap$: Observable<Map<string, Bookmark>>;

  @Input() bookmark: Bookmark;
  @Input() logged = true;
  @Input() trackingData: AlgoliaTrackerInterface;

  @Output() toggleBookmark = new EventEmitter<Bookmark>();
  @Output() applyButtonHidden = new EventEmitter<boolean>();
  @Output() externalApplication = new EventEmitter<void>();

  locale = environment.locale;
  LocaleEnum = LocaleEnum;
  readonly wizbiiDomain: string;

  similarJobs$: Observable<Job[]>;

  applyLink: RouterLinkConfig;
  mission: string;
  validThrough: Date;
  descriptionCompany: string;
  metaImageUrl: string;

  trackingDataMap$: Observable<Map<string, EventTrackerInterface>>;

  companyPath: string = buildPath(FeaturesRoutingEnum.Company);
  secureExternalLinks = secureExternalLinks;

  accountSignupUrlJob: string;

  shareLinks: ShareLink[] = [];

  private _model: Job;

  readonly isMobile$: Observable<boolean>;

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  get validThroughStr(): string | undefined {
    return this.validThrough ? dayjs(this.validThrough).toISOString() : undefined;
  }

  get createdDateStr(): string | undefined {
    return this.model?.createdDate ? dayjs(this.model.createdDate).toISOString() : undefined;
  }

  constructor(
    private readonly htmlParser: HtmlParseService,
    private readonly eventTrackerService: EventTrackerService,
    private readonly jobWebservice: JobWebservice,
    @Inject(WINDOW) private readonly window: any,
    @Optional() @Inject(REQUEST) private readonly request: Request,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>,
    private readonly sanitizer: DomSanitizer,
    private readonly breakpointObserver: BreakpointObserver
  ) {
    this.wizbiiDomain = environment.apiDomain;

    this.isMobile$ = this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
      startWith({ matches: true }),
      map((result) => result.matches),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  @Input()
  get model(): Job {
    return this._model;
  }

  set model(job: Job) {
    this._model = job;

    const { descriptionCompany, company, mission, online } = job;
    const { id: companyId, logoUrl } = company;

    this.applyLink = this.getApplyLink(job);
    this.descriptionCompany = this.htmlParser.parse(descriptionCompany);
    this.mission = this.htmlParser.cleanSchema(mission); // remove itemscope/itemprop/itemtype messing with our schemas

    this.validThrough = online
      ? new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 60)
      : new Date(new Date().getTime() - 1000 * 60 * 60 * 24);

    this.metaImageUrl = logoUrl && buildUrl(`${environment.api.images}/company/logo`, { slug: companyId });

    const redirect =
      this.request && isPlatformServer(this.platformId)
        ? `${this.request.protocol}://${this.request.get('host')}${this.request.url}`
        : `${this.window.location.origin}${this.window.location.pathname}`;

    this.accountSignupUrlJob = `${environment.urls.accountSignup}${redirect}&context=wizbii-jobs-offer&job-id=${job.id}`;

    // Construct job's permalink
    const permalink = `https://www.wizbii.com/company/${job.company.id}/job/${job.id}`;

    // Compute sharing URL for each social media
    this.shareLinks = socialMedia.map((item) => {
      // Invoke `getParams` function and build URL
      const params = item.getShareParams(permalink, job);
      const url = buildUrl(item.shareUrl, params);

      // Trust URL so Angular doesn't prepend `unsafe:` when protocol is unknown
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);

      return {
        media: item,
        url: safeUrl,
      };
    });
  }

  ngOnInit(): void {
    this.similarJobs$ = this.jobWebservice.getSimilarJobs(this.model.id).pipe(shareReplay(1));

    this.trackingDataMap$ = combineLatest([this.isLogged$, this.similarJobs$]).pipe(
      map(([isLogged, jobs = []]) =>
        jobs.reduce(
          (data, job) =>
            data.set(job.id, {
              eventCategory: 'Job Page',
              eventAction: 'Similar Job',
              eventLabel: isLogged ? 'Logged' : 'Unlogged',
            }),
          new Map<string, EventTrackerInterface>()
        )
      )
    );
  }

  trackByJob(_: number, job: Job): string {
    return job.id;
  }

  trackEvent(event: EventTrackerInterface): void {
    this.eventTrackerService.send(event);
  }

  private getApplyLink(job: Job): RouterLinkConfig {
    const { id: jobId, externalUrl, company } = job;
    const { id: companyId } = company;

    if (externalUrl) {
      return {
        routerLink: buildUrl(buildPath(FeaturesRoutingEnum.Redirection), { jobId }),
        target: '_blank',
      };
    }

    return {
      routerLink: buildUrl(buildPath(FeaturesRoutingEnum.Job, JobRoutingEnum.Apply), { companyId, jobId }),
      queryParamsHandling: 'preserve',
      fragment: 'layout-main',
    };
  }

  handleApplyButtonClick(): void {
    if (!this._model.externalUrl) {
      return;
    }

    const { id: ids, title: name } = this._model;
    this.eventTrackerService.sendCustomTrackingEventToFacebookPixel({ ids, name, category: 'Page Job' });
    this.externalApplication.emit();
  }

  trackByLabel(_: number, link: ShareLink): string {
    return link.media.label;
  }

  @Dispatch() toggleBookmarkDispatch = (job: Job): Observable<BookmarkToggle> =>
    this.isLogged$.pipe(
      take(1),
      map((isLogged) => {
        this.eventTrackerService.send({
          eventCategory: 'Job Page',
          eventAction: 'Click Save Jobs Similaire',
          eventLabel: isLogged ? 'Logged' : 'Unlogged',
        });

        return new BookmarkToggle(job.id);
      })
    );
}
